<template>
  <div class="modal fade" id="ipChangeHistoryModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">IP Change History</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3 text-sm text-gray-300">
            {{ license?.proxy_name || license?.package_name || 'Selected proxy' }}
          </div>
          <v-data-table
            :headers="headers"
            :items="history"
            :loading="loading"
            item-key="uuid"
            items-per-page="-1"
            hide-default-footer
            class="border bg-white text-black"
            style="font-size: 13px;"
          >
            <template #item="{ item }">
              <tr>
                <td>{{ formatDate(item.createdAt) }}</td>
                <td>{{ sourceText(item.source) }}</td>
                <td>{{ statusText(item.status) }}</td>
                <td>{{ historyIpText(item) }}</td>
              </tr>
            </template>
            <template #no-data>
              <div class="py-5 text-center">No IP changes recorded yet.</div>
            </template>
            <template #bottom></template>
          </v-data-table>
          <div class="d-flex align-items-center justify-content-between mt-3">
            <span class="text-sm">Showing {{ history.length }} of {{ total }}</span>
            <v-btn
              v-if="history.length < total"
              class="bg-dark text-light"
              :loading="loading"
              @click="loadMore"
            >
              Load More
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useProxyStore } from '@/stores';

const props = defineProps({
  license: {
    type: Object,
    default: null,
  },
});

const proxyStore = useProxyStore();
const history = ref([]);
const total = ref(0);
const loading = ref(false);
const pageSize = 50;

const isEnabledFlag = (value) => value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

const isIpv6License = computed(() => (
  isEnabledFlag(props.license?.is_ipv6)
  || (props.license?.m_type === 'PROXYSMART' && isEnabledFlag(props.license?.m_ipv6))
));

const headers = computed(() => [
  { title: 'Date', key: 'createdAt', align: 'center' },
  { title: 'Source', key: 'source', align: 'center' },
  { title: 'Status', key: 'status', align: 'center' },
  { title: isIpv6License.value ? 'IPv6' : 'IPv4', key: isIpv6License.value ? 'ipv6' : 'ipv4', align: 'center' },
]);

const loadHistory = async (from = 0) => {
  if (!props.license?.uuid) return;
  loading.value = true;
  const res = await proxyStore.loadIpChangeHistory(props.license.uuid, { from, count: pageSize });
  const rows = res?.histories || [];
  history.value = from === 0 ? rows : [...history.value, ...rows];
  total.value = res?.total || rows.length;
  loading.value = false;
};

const loadMore = () => {
  loadHistory(history.value.length);
};

const sourceText = (source) => ({
  customer_change_ip: 'Customer change',
  change_ip_url: 'Change IP URL',
  admin_change_ip: 'Admin change',
  auto_rotation: 'Auto rotation',
  xproxy_rotation_log: 'System rotation log',
  proxysmart_rotation_log: 'System rotation log',
  refresh_ip: 'Refresh',
}[source] || source || '-');

const statusText = (status) => ({
  success: 'Success',
  submitted: 'In Progress',
  failed: 'Failed',
}[status] || status || '-');

const ipText = (oldValue, newValue, status) => {
  if (!oldValue && !newValue) return '-';
  if (status === 'submitted') {
    const currentValue = oldValue || newValue;
    return currentValue ? `${currentValue} -> pending` : '-';
  }
  if (oldValue === newValue) return newValue || oldValue || '-';
  return `${oldValue || '-'} -> ${newValue || '-'}`;
};

const historyIpText = (item) => (
  isIpv6License.value
    ? ipText(item.old_public_ipv6, item.new_public_ipv6, item.status)
    : ipText(item.old_public_ip, item.new_public_ip, item.status)
);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const handleShown = () => loadHistory(0);

watch(() => props.license?.uuid, () => {
  history.value = [];
  total.value = 0;
});

onMounted(() => {
  document.getElementById('ipChangeHistoryModal')?.addEventListener('shown.bs.modal', handleShown);
});

onBeforeUnmount(() => {
  document.getElementById('ipChangeHistoryModal')?.removeEventListener('shown.bs.modal', handleShown);
});
</script>
